import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import PageLayout from "../../components/PageLayout/PageLayout";
import OfferList from "./OfferList/OfferList";

import { AppDispatch } from "../../app/store";

import {
  getAdminOffers,
  selectAdminOffers,
  selectAdminOffersLoading,
  selectAdminOffersError,
  AdminOffer,
} from "../../slices/adminOffer/adminOfferSlice";

import styles from "./AdminOffers.module.css";

type StatusFilter =
  | "all"
  | "active"
  | "inactive"
  | "expiring"
  | "expired";

type AssignmentFilter =
  | "all"
  | "assigned"
  | "unassigned";

const AdminOffers: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const offers = useSelector(selectAdminOffers);
  const loading = useSelector(
    selectAdminOffersLoading
  );
  const error = useSelector(
    selectAdminOffersError
  );

  const [statusFilter, setStatusFilter] =
    useState<StatusFilter>("all");

  const [
    assignmentFilter,
    setAssignmentFilter,
  ] = useState<AssignmentFilter>("all");

  useEffect(() => {
    dispatch(getAdminOffers());
  }, [dispatch]);

  const getOfferStatus = (
    offer: AdminOffer
  ): Exclude<StatusFilter, "all"> => {
    if (!offer.isActive) {
      return "inactive";
    }

    if (!offer.expirationDate) {
      return "active";
    }

    const now = new Date();

    const expiration = new Date(
      offer.expirationDate
    );

    if (
      expiration.getTime() < now.getTime()
    ) {
      return "expired";
    }

    const difference =
      expiration.getTime() - now.getTime();

    const daysLeft = Math.ceil(
      difference / (1000 * 60 * 60 * 24)
    );

    if (daysLeft <= 30) {
      return "expiring";
    }

    return "active";
  };

  const filteredOffers = offers.filter(
    (offer) => {
      const offerStatus =
        getOfferStatus(offer);

      const matchesStatus =
        statusFilter === "all" ||
        offerStatus === statusFilter;

      const matchesAssignment =
        assignmentFilter === "all" ||
        (assignmentFilter === "assigned" &&
          offer.isAssigned) ||
        (assignmentFilter ===
          "unassigned" &&
          !offer.isAssigned);

      return (
        matchesStatus &&
        matchesAssignment
      );
    }
  );

  const clearFilters = () => {
    setStatusFilter("all");
    setAssignmentFilter("all");
  };

  const filtersActive =
    statusFilter !== "all" ||
    assignmentFilter !== "all";

  return (
    <PageLayout
      className={styles.AdminOffers}
    >
      <h1 className={styles.Title}>
        Wizytówki firm
      </h1>

      <div className={styles.InfoBox}>
        <div className={styles.InfoTitle}>
          Zarządzanie wizytówkami
        </div>

        <div className={styles.InfoText}>
          Przeglądaj wizytówki firm, ich
          właścicieli oraz terminy ważności.
        </div>
      </div>

      {!loading && !error && (
        <div className={styles.Filters}>
          <div
            className={styles.FilterGroup}
          >
            <label
              className={styles.FilterLabel}
            >
              Status
            </label>

            <select
              className={styles.Select}
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  e.target
                    .value as StatusFilter
                )
              }
            >
              <option value="all">
                Wszystkie
              </option>

              <option value="active">
                Aktywne
              </option>

              <option value="inactive">
                Nieaktywne
              </option>

              <option value="expiring">
                Bliskie przedawnieniu
              </option>

              <option value="expired">
                Przedawnione
              </option>
            </select>
          </div>

          <div
            className={styles.FilterGroup}
          >
            <label
              className={styles.FilterLabel}
            >
              Przypisanie
            </label>

            <select
              className={styles.Select}
              value={assignmentFilter}
              onChange={(e) =>
                setAssignmentFilter(
                  e.target
                    .value as AssignmentFilter
                )
              }
            >
              <option value="all">
                Wszystkie
              </option>

              <option value="assigned">
                Przypisane
              </option>

              <option value="unassigned">
                Nieprzypisane
              </option>
            </select>
          </div>

          <div
            className={styles.FilterSummary}
          >
            <span>
              Wyświetlono{" "}
              <strong>
                {filteredOffers.length}
              </strong>{" "}
              z{" "}
              <strong>
                {offers.length}
              </strong>
            </span>

            {filtersActive && (
              <button
                type="button"
                className={
                  styles.ClearButton
                }
                onClick={clearFilters}
              >
                Wyczyść filtry
              </button>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className={styles.Message}>
          Pobieranie wizytówek...
        </div>
      )}

      {!loading && error && (
        <div className={styles.Error}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <OfferList
          offers={filteredOffers}
        />
      )}
    </PageLayout>
  );
};

export default AdminOffers;